import {
  DateTimeInput,
  Form,
  Grid,
  Input,
  ISelectOption,
  Select,
  SingleColumnLayout,
} from '@salecycle/ui-component-library';
import { Campaign } from 'entities/campaign';
import { getActivityState } from 'entities/lifecycle/activityState';
import { ObjectiveLabels, RulesetSplitTest } from 'entities/rulesetSplitTest';
import * as React from 'react';
import { connect } from 'react-redux';
import { Selectors as RouterSelectors } from 'router/';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import {
  Actions as SplitTestActions,
  Selectors as SplitTestSelectors,
} from 'sections/campaigns/state/rulesetSplitTests';
import { State } from 'store/reducer';
import { NewWizardSplitTestTemporaryId } from '../constants';
import {
  isSuccessfulValidation,
  validateSplitTestCampaignId,
  validateSplitTestEndDate,
  validateSplitTestName,
  validateSplitTestRulesetId,
  validateSplitTestStartDate,
} from '../splitTestValidator';

interface Option {
  id: string;
  label: string;
}

interface StateProps {
  splitTest: RulesetSplitTest;
  campaigns: Option[];
  rulesets: Option[];
  timezoneOffset: number;
  splitTestId: string;
  otherSiteSplitTests: RulesetSplitTest[];
}

interface DispatchProps {
  updateLocalSplitTest: (id: string, entity: RulesetSplitTest) => void;
}

export type Props = StateProps & DispatchProps;

export const SplitTestSettingsForm: React.FC<Props> = props => {
  const onNameChange = (name: string) => props.updateLocalSplitTest(props.splitTestId, { name });
  const onStartDateChange = (startTime: number) => props.updateLocalSplitTest(props.splitTestId, { startTime });
  const onEndDateChange = (endTime: number) => props.updateLocalSplitTest(props.splitTestId, { endTime });

  const onCampaignChange = (item: ISelectOption<string>) => {
    props.updateLocalSplitTest(props.splitTestId, { campaignId: item.value, rulesetId: undefined });
  };
  const onRulesetChange = (item: ISelectOption<string>) => {
    props.updateLocalSplitTest(props.splitTestId, { rulesetId: item.value });
  };

  const splitTestNameResult = validateSplitTestName(props.splitTest, props.otherSiteSplitTests);
  const splitTestCampaignIdResult = validateSplitTestCampaignId(props.splitTest);
  const splitTestRulesetIdResult = validateSplitTestRulesetId(props.splitTest);
  const splitTestStartDateResult = validateSplitTestStartDate(
    props.splitTest,
    props.campaigns.find(camp => camp.id === props.splitTest.campaignId),
  );
  const splitTestEndDateResult = validateSplitTestEndDate(
    props.splitTest,
    props.campaigns.find(camp => camp.id === props.splitTest.campaignId),
  );

  const splitTestNameValidationState = splitTestNameResult.isValid ? 'complete' : 'warning';
  const splitTestCampaignIdValidationState = splitTestCampaignIdResult.isValid ? 'complete' : 'warning';
  const splitTestRulesetIdValidationState = splitTestRulesetIdResult.isValid ? 'complete' : 'warning';
  const splitTestStartDateValidationState = splitTestStartDateResult.isValid ? 'complete' : 'warning';
  const splitTestEndDateValidationState = splitTestEndDateResult.isValid ? 'complete' : 'warning';

  const splitTestNameErrorMessage = isSuccessfulValidation(splitTestNameResult)
    ? undefined
    : splitTestNameResult.errorMessage;
  const splitTestStartDateErrorMessage = isSuccessfulValidation(splitTestStartDateResult)
    ? undefined
    : splitTestStartDateResult.errorMessage;
  const splitTestEndDateErrorMessage = isSuccessfulValidation(splitTestEndDateResult)
    ? undefined
    : splitTestEndDateResult.errorMessage;

  const campaignOptions = props.campaigns.map(campaign => {
    return { value: campaign.id, label: campaign.label };
  });
  const campaignValue = campaignOptions
    .filter(item => item.value === props.splitTest.campaignId)
    .reduce((acc, item) => item, null);

  const rulesetOptions = props.rulesets.map(campaign => {
    return { value: campaign.id, label: campaign.label };
  });
  const rulesetValue = rulesetOptions
    .filter(item => item.value === props.splitTest.rulesetId)
    .reduce((acc, item) => item, null);

  return (
    <SingleColumnLayout heading="Split Test Settings">
      <Form>
        <Input
          label="Split Test Name"
          ariaLabel="Split Test Name"
          inputId="SplitTestNameInput"
          value={props.splitTest.name}
          required={true}
          onChange={onNameChange}
          validationState={(!!props.splitTest.name && splitTestNameValidationState) || undefined}
          validationMessage={splitTestNameErrorMessage}
        />
        <Select
          ariaLabel="Campaign name"
          label="Campaign name"
          inputId="CampaignNameSelect"
          options={campaignOptions}
          value={campaignValue}
          onChange={onCampaignChange}
          validationState={(!!props.splitTest.campaignId && splitTestCampaignIdValidationState) || undefined}
        />
        <Select
          ariaLabel="Ruleset"
          label="Ruleset"
          inputId="RulesetSelect"
          options={rulesetOptions}
          value={rulesetValue}
          onChange={onRulesetChange}
          validationState={(!!props.splitTest.rulesetId && splitTestRulesetIdValidationState) || undefined}
        />
        <Grid>
          <DateTimeInput
            label="Start Date"
            value={props.splitTest.startTime}
            offset={props.timezoneOffset}
            onChange={onStartDateChange}
            validationState={(!!props.splitTest.startTime && splitTestStartDateValidationState) || undefined}
            validationMessage={splitTestStartDateErrorMessage}
          />
          <DateTimeInput
            label="End Date"
            value={props.splitTest.endTime}
            offset={props.timezoneOffset}
            onChange={onEndDateChange}
            validationState={(!!props.splitTest.endTime && splitTestEndDateValidationState) || undefined}
            validationMessage={splitTestEndDateErrorMessage}
          />
        </Grid>
        <Input
          ariaLabel="What are you testing?"
          label="What are you testing?"
          inputId="WhatAreYouTestingInput"
          value={props.splitTest.type}
          disabled={true}
        />
        <Input
          label="What is your goal?"
          ariaLabel="What is your goal?"
          inputId="WhatIsYourGoalInput"
          value={ObjectiveLabels[props.splitTest.objective]}
          disabled={true}
        />
      </Form>
    </SingleColumnLayout>
  );
};

const Actions: DispatchProps = {
  updateLocalSplitTest: SplitTestActions.updateLocal,
};

export const mapStateToProps = (state: State): StateProps => {
  const campaigns = CampaignSelectors.getEntityMap(state);

  const campaignOptions: Option[] = Object.keys(campaigns)
    .filter(id => {
      const campaign: Campaign = campaigns[id];
      return (
        campaign.state === 'ACTIVE' &&
        getActivityState(campaign) !== 'Finished' &&
        campaign.rulesetPriorities.length > 0
      );
    })
    .map(id => ({
      id,
      label: campaigns[id].name,
    }));

  const splitTestId = RouterSelectors.getSplitTestId(state) || NewWizardSplitTestTemporaryId;
  const splitTest = SplitTestSelectors.getUpdatedEntity(state, splitTestId);
  const rulesets = RulesetSelectors.getEntityMap(state);
  const rulesetOptions = Object.keys(rulesets)
    .filter(x => rulesets[x].campaignId === splitTest.campaignId)
    .map(id => ({
      id,
      label: rulesets[id].name,
    }));
  return {
    splitTest,
    otherSiteSplitTests: SplitTestSelectors.otherSplitTests(state, splitTestId),
    campaigns: campaignOptions,
    rulesets: rulesetOptions,
    timezoneOffset: 0,
    splitTestId,
  };
};

export default connect(mapStateToProps, Actions)(SplitTestSettingsForm);
